/**
 * MKS-339 — insert a canned reply into the comment composer.
 *
 * A `<details>` dropdown, matching `AssigneePicker`: a11y, keyboard and
 * outside-click-to-close come from the native element rather than a popover
 * library.
 *
 * Insertion rules:
 *   - The snippet is rendered with `{requester_name}` / `{ticket_code}`
 *     substituted from `context` BEFORE it reaches the textarea, so the agent
 *     reads what the client will read and can edit it.
 *   - It is inserted at the caret, not appended, so a reply can be dropped
 *     between two paragraphs the agent already wrote.
 *   - The caret lands after the inserted text — the next keystroke continues
 *     the reply instead of jumping to the end of the box.
 *
 * Renders nothing at all when the project has no replies: an always-visible
 * control that opens an empty list is worse than no control.
 */
import { useRef, type ComponentRef } from 'react';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { useCannedReplies } from '../../hooks/useHelpdesk';
import { renderCannedReply } from '../../services/api/helpdesk';

export interface CannedReplyContext {
  requesterName?: string | null;
  ticketCode?: string | null;
}

export interface CannedReplyPickerProps {
  projectId: number;
  /** Values for `{requester_name}` / `{ticket_code}`. */
  context: CannedReplyContext;
  /**
   * Called with the rendered snippet. The composer owns the textarea, so it
   * is the composer that splices the text in at the caret.
   */
  onInsert: (text: string) => void;
  disabled?: boolean;
  /** False while the helpdesk release flag is off — the endpoint 404s there. */
  enabled?: boolean;
}

export function CannedReplyPicker({
  projectId,
  context,
  onInsert,
  disabled,
  enabled = true,
}: CannedReplyPickerProps) {
  const detailsRef = useRef<ComponentRef<'details'> | null>(null);
  const { data: replies = [] } = useCannedReplies(projectId, enabled);

  if (!enabled || replies.length === 0) return null;

  return (
    <details ref={detailsRef} className="relative" data-testid="canned-reply-picker">
      <summary
        className={`list-none cursor-pointer text-gray-500 hover:text-gray-700 ${
          disabled ? 'opacity-50 pointer-events-none' : ''
        }`}
        aria-label="Insert canned reply"
      >
        <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
      </summary>
      <div className="absolute left-0 bottom-full mb-1 z-20 w-72 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
        {replies.map((reply) => (
          <button
            key={reply.id}
            type="button"
            className="w-full text-left px-3 py-2 hover:bg-gray-50"
            onClick={() => {
              onInsert(renderCannedReply(reply.body, context));
              if (detailsRef.current) detailsRef.current.open = false;
            }}
          >
            <span className="block text-sm text-gray-900">{reply.title}</span>
            <span className="block text-xs text-gray-500 truncate">{reply.body}</span>
          </button>
        ))}
      </div>
    </details>
  );
}
